import React, { FC, useEffect, useRef, useState } from 'react'
import { flatten } from 'lodash'
import Loading from '../common/Loading'
import { useTranslation } from 'react-i18next'
import { ReactSVG } from 'react-svg'
import AppDetail from './AppDetail'
import { buildWorkTime, secToHour, secToTwoDigitsMinute } from '../../../utils/date'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useDispatch, useSelector } from 'react-redux'
import { updateSearchResult } from '../../../states/containers/search/listSlice'

import iconv = require('iconv-lite')
import { RootState } from '../../../states/containers/store'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const FileDownload = require('js-file-download')

type LangValue = 'ja' | 'en'

interface SearchValues {
  start_date: string
  end_date: string
  team_names?: string[]
  member_names?: string[]
  keywords?: string[]
  service_names?: string[]
  category_names?: string[]
  efficiency_level?: string[],
  stress_level?: string[],
  focus_level?: string[],
  lower_limit_time?: number,
  sort_key?: string
  sort_type?: string,
}

interface SearchRequestParams {
  start_date: string
  end_date: string
  team_ids: number[]
  member_ids: number[]
  keywords: string[]
  service_names: string[]
  category_ids: number[]
  efficiency_level?: string[],
  stress_level?: string[],
  focus_level?: string[],
  lower_limit_time?: number
  sort_key?: string
  sort_type?: string
}

interface SearchResult {
  total: {
    time: number
    biz: number
    count: number
    efficiency: {
      score: number
      judgement: string
    }
    focus: {
      score: number
      judgement: string
    }
    stress: {
      score: number
      judgement: string
    }
  },
  service: {
    icon: string
    color: string
    icon_color: string
    name: string
    url: string
    title: string
  },
  members: [
    {
      id: number
      name: string
      date: string
      biz: number
      time: number
      count: number
      efficiency: {
        score: number
        judgement: string
      }
      focus: {
        score: number
        judgement: string
      }
      stress: {
        score: number
        judgement: string
      }
    }
  ]
}

interface SearchResponseParams {
  summary: {
    biz: {
      total_value: number
      name_labels: string[]
      value_labels: number[]
    },
    elapsed_time: {
      total_value: number
      name_labels: string[]
      value_labels: number[]
    },
    count: {
      total_value: number
      name_labels: string[]
      value_labels: number[]
    }
  } | undefined,
  result_list: SearchResult[] | undefined
  tags: string[]
  search_result: boolean
  search_request: SearchRequestParams | undefined
}

interface Props {
  searchValue: SearchValues
  searchResult: SearchResponseParams
  isLoading: boolean
  displayGraph: boolean
}

type SortList =
  "time-asc"
  | "time-desc"
  | "biz-asc"
  | "biz-desc"
  | "count-asc"
  | "count-desc"
  | "efficiency-asc"
  | "efficiency-desc"
  | "stress-asc"
  | "stress-desc"
  | "focus-asc"
  | "focus-desc"

type SortKey =
  "time"
  | "biz"
  | "count"
  | "efficiency"
  | "stress"
  | "focus"

const SearchResult: FC<Props> = (props) => {
  const {
    searchValue,
    searchResult,
    isLoading,
    displayGraph,
  } = props

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const {
    commonPart,
  } = useSelector((state: RootState) => state)

  const RESULT_DISPLAY_LIMIT = 10

  const [displayCsvModal, setDisplayCsvModal] = useState(false)
  const [displayResultList, setDisplayResultList] = useState<SearchResult[]>([])
  const [filterList, setFilterList] = useState<string[]>([])
  const [resultNum, setResultNum] = useState<number>(0)
  const [isDisplayTagArea, setIsDisplayTagArea] = useState<boolean>(false)
  const [isDisplayTagButton, setIsDisplayTagButton] = useState<boolean>(false)
  const [isResultLoading, setIsResultLoading] = useState<boolean>(false)
  const [displayOffset, setDisplayOffset] = useState(0)
  const [isMoreLoading, setIsMoreLoading] = useState(false)

  const displayOffsetRef = useRef<number>(0)
  const displayResultListRef = useRef<SearchResult[]>([])
  const [isDisplayGraphDetail, setIsDisplayGraphDetail] = useState<boolean>(false)
  const [maxSummaryElapsedTimeValueLabel, setMaxSummaryElapsedTimeValueLabel] = useState<number>(0)
  const [maxSummaryBizValueLabel, setMaxSummaryBizValueLabel] = useState<number>(0)
  const [maxSummaryCountValueLabel, setMaxSummaryCountValueLabel] = useState<number>(0)

  displayOffsetRef.current = displayOffset
  displayResultListRef.current = displayResultList
  const tagRef = useRef<HTMLDivElement>(null)

  const downloadCsv = (code: string) => {
    const resultRows = flatten(searchResult.result_list?.map((result) => (
      result.members.map((member) => {
        const application = result.service.name ? result.service.name.replace(/"/g, '""').replace(/\n/g, '') : ""
        const title = result.service.title ? result.service.title.replace(/"/g, '""').replace(/\n/g, '') : ""
        const url = result.service.url ? result.service.url.replace(/"/g, '""') : ""
        // eslint-disable-next-line max-len
        return `"${application}","${title}","${url}","${member.date}","${member.name}","${buildWorkTime(member.time)}","${member.biz}","${member.count}"`
      })
    )))
    resultRows.unshift(`アプリケーション,タイトル,URL,日付,メンバー,作業時間,操作量,回数`)
    const filename = `search_result_${searchValue.start_date}-${searchValue.end_date}`.slice(0, 150)
    if (code === 'Shift-JIS') {
      const encodeData = iconv.encode(resultRows.join('\n'), "windows-31j")
      FileDownload(new Blob([encodeData]), `${filename}.csv`)
    } else {
      FileDownload(resultRows.join('\n'), `${filename}.csv`)
    }
  }

  const sortResultList = (sort_key: SortList) => {
    if (!searchResult.result_list || !searchResult.search_request) return
    setIsResultLoading(true)
    let tmpSortList: SearchResult[] = []
    const key = sort_key.split("-")
    const keyName: SortKey = key[0] as SortKey
    if (key[1] === "desc") {
      if (keyName === "efficiency" || keyName === "stress" || keyName === "focus") {
        tmpSortList = [...searchResult.result_list].sort((val_1, val_2) => {
          if (val_1.total[keyName].score < val_2.total[keyName].score) return 1
          if (val_1.total[keyName].score > val_2.total[keyName].score) return -1
          return 0
        })
      } else {
        tmpSortList = [...searchResult.result_list].sort((val_1, val_2) => {
          if (val_1.total[keyName] < val_2.total[keyName]) return 1
          if (val_1.total[keyName] > val_2.total[keyName]) return -1
          return 0
        })
      }
    } else if (key[1] === "asc") {
      if (keyName === "efficiency" || keyName === "stress" || keyName === "focus") {
        tmpSortList = [...searchResult.result_list].sort((val_1, val_2) => {
          if (val_1.total[keyName].score > val_2.total[keyName].score) return 1
          if (val_1.total[keyName].score < val_2.total[keyName].score) return -1
          return 0
        })
      } else {
        tmpSortList = [...searchResult.result_list].sort((val_1, val_2) => {
          if (val_1.total[keyName] > val_2.total[keyName]) return 1
          if (val_1.total[keyName] < val_2.total[keyName]) return -1
          return 0
        })
      }
    }
    dispatch(updateSearchResult(
      {
        result_list: tmpSortList,
        sort_key: keyName,
        sort_type: key[1],
      },
      () => {
        setIsResultLoading(false)
      },
    ))
  }

  const buildFilterList = (title: string) => {
    if (!title || !searchResult.result_list) return
    setIsResultLoading(true)
    let tmpFilterList: string[] = []
    if (filterList?.includes(title)) {
      tmpFilterList = filterList.filter((value) => value !== title)
    } else {
      tmpFilterList = [...filterList, title]
    }
    setFilterList(tmpFilterList)
  }

  const clickMoreButton = (offset: number) => {
    if (!displayResultListRef.current || displayResultListRef.current.length < offset) return
    setIsMoreLoading(true)
    setTimeout(() => {
      setDisplayOffset(offset + RESULT_DISPLAY_LIMIT)
    }, 500)
  }

  const currentScroll = (): number => Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop)
  const BottomHeight = (): number => document.getElementsByClassName("main")[0].clientHeight
  const windowHeight = (): number => document.documentElement.clientHeight

  const onScroll = (): void => {
    if (BottomHeight() < (currentScroll() + windowHeight())) {
      clickMoreButton(displayOffsetRef.current)
    }
  }

  useEffect(() => {
    document.addEventListener("scroll", onScroll)
    return () => {
      document.removeEventListener("scroll", onScroll)
    }
  }, [])

  useEffect(() => {
    if (!searchResult.result_list) return
    let tmpResultList = []
    if (filterList.length) {
      tmpResultList = searchResult.result_list.filter((result) => filterList.includes(result.service.name))
    } else {
      tmpResultList = searchResult.result_list
    }
    setResultNum(tmpResultList.length)
    const addList = tmpResultList.slice(0, RESULT_DISPLAY_LIMIT)
    setDisplayResultList(addList)
    setIsResultLoading(false)
    setDisplayOffset(RESULT_DISPLAY_LIMIT)
  }, [filterList])

  useEffect(() => {
    if (!searchResult.result_list) return
    let tmpResultList = []
    if (filterList.length) {
      tmpResultList = searchResult.result_list.filter((result) => filterList.includes(result.service.name))
    } else {
      tmpResultList = searchResult.result_list
    }
    setResultNum(tmpResultList.length)
    const addList = tmpResultList.slice(0, RESULT_DISPLAY_LIMIT)
    setDisplayResultList(addList)
    setDisplayOffset(RESULT_DISPLAY_LIMIT)
  }, [searchResult.result_list])

  useEffect(() => {
    if (!searchResult.result_list) return
    setIsDisplayTagButton(false)
    setFilterList([])
  }, [searchResult.search_request])

  useEffect(() => {
    if (!tagRef.current) return
    if (tagRef.current.offsetHeight > 38) {
      setIsDisplayTagButton(true)
    } else {
      setIsDisplayTagButton(false)
    }
  }, [isLoading])

  useEffect(() => {
    if (!searchResult.result_list || displayOffset === RESULT_DISPLAY_LIMIT) return
    let tmpResultList = []
    if (filterList.length) {
      tmpResultList = searchResult.result_list.filter((result) => filterList.includes(result.service.name))
    } else {
      tmpResultList = searchResult.result_list
    }
    setResultNum(tmpResultList.length)
    const addList = tmpResultList.slice(displayOffset - RESULT_DISPLAY_LIMIT, displayOffset)
    setIsMoreLoading(false)
    setDisplayResultList(displayResultList?.concat(addList))
  }, [displayOffset])

  useEffect(() => {
    if (searchResult.summary) {
      setMaxSummaryCountValueLabel(Math.max(...searchResult.summary.count.value_labels))
      setMaxSummaryBizValueLabel(Math.max(...searchResult.summary.biz.value_labels))
      setMaxSummaryElapsedTimeValueLabel(Math.max(...searchResult.summary.elapsed_time.value_labels))
    }
  }, [searchResult.summary])

  const renderSearchResult = () => (
    <>
      <section className="content-header search_result_num between">
        <div
          className="result_title"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: t('search.result.title', { num: searchResult.result_list?.length }) }}
        />
        <div className="download_csv_area">
          <button
            type="button"
            className="toggle"
            onClick={() => setDisplayCsvModal(true)}
          >
            <ReactSVG
              src="/static/images/icon/icon-download.svg"
              className="icon"
            />
            {t('search.result.csv.button.download')}
          </button>
          <div className={`download_csv_modal ${displayCsvModal || "none"}`}>
            <div className="title">
              {t('search.result.csv.title')}
            </div>
            {
              commonPart.payment_finished ? (
                <div className="detail">
                  <div className="button_area">
                    <button
                      type="button"
                      className="download"
                      onClick={() => downloadCsv('Shift-JIS')}
                    >
                      {t('search.result.csv.button.shift-jis')}
                    </button>
                    <button
                      type="button"
                      className="download"
                      onClick={() => downloadCsv('UTF-8')}
                    >
                      {t('search.result.csv.button.utf-8')}
                    </button>
                  </div>
                  <div className="comment">
                    {t('search.result.csv.comment')}
                  </div>
                </div>
              ) : (
                <div className="detail">
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666666",
                      marginBottom: "16px",
                      textAlign: "center",
                    }}
                  >
                    トライアル期間中はご利用になれません
                  </div>
                  <div className="button_area">
                    <button
                      type="button"
                      className="download"
                      disabled
                    >
                      {t('search.result.csv.button.shift-jis')}
                    </button>
                    <button
                      type="button"
                      className="download"
                      disabled
                    >
                      {t('search.result.csv.button.utf-8')}
                    </button>
                  </div>
                </div>
              )
            }
          </div>
          <div className={`close_screen gray ${displayCsvModal || 'none'}`} role="presentation" onClick={() => setDisplayCsvModal(false)} />
        </div>
      </section>
      {
        searchResult.result_list?.length === 0 ? (
          <div className="result_no_data">
            {t('search.result.no_data')}
          </div>
        ) : (
          <section className="content content-area">
            <div className="result_wrapper">
              <div className="summary">
                <div className="summary_title">
                  検索条件の合計値
                </div>
                <div className="graph_area">
                  <div className="graph_detail_wrapper">
                    <div className="graph_detail">
                      <span className="head">
                        {t('search.result.summary.title.time')}
                      </span>
                      <span className="total">
                        {t('search.result.summary.title.total')}
                      </span>
                      <span className="hour">
                        {secToHour(searchResult.summary?.elapsed_time.total_value || 0)}
                      </span>
                      {t('search.result.hour')}
                      <span className="minute">
                        {secToTwoDigitsMinute(searchResult.summary?.elapsed_time.total_value || 0)}
                      </span>
                      {t('search.result.minute')}
                    </div>
                  </div>
                  <div className="graph_detail_wrapper">
                    <div className="graph_detail">
                      <span className="head">
                        {t('search.result.summary.title.biz')}
                      </span>
                      <span className="total">
                        {t('search.result.summary.title.total')}
                      </span>
                      <span className="biz">
                        {searchResult.summary?.biz.total_value.toLocaleString()}
                      </span>
                      {t('search.result.biz')}
                    </div>
                  </div>
                  <div className="graph_detail_wrapper">
                    <div className="graph_detail">
                      <span className="head">
                        {t('search.result.summary.title.count')}
                      </span>
                      <span className="total">
                        {t('search.result.summary.title.total')}
                      </span>
                      <span className="count">
                        {searchResult.summary?.count.total_value.toLocaleString()}
                      </span>
                      {t('search.result.count')}
                    </div>
                  </div>
                </div>
                {
                  !isDisplayGraphDetail ? (
                    <div className="block graph_area_toggle_wrapper center">
                      <div
                        onClick={() => setIsDisplayGraphDetail(!isDisplayGraphDetail)}
                        role="none"
                        className={`graph_area_toggle ${isDisplayGraphDetail && "checked"}`}
                      >
                        {
                          isDisplayGraphDetail ? (
                            t('search.result.summary.graph_area_toggle.title_close')
                          ) : (
                            t('search.result.summary.graph_area_toggle.title')
                          )
                        }
                        <div className="icon">
                          <FontAwesomeIcon icon={['fas', 'angle-down']} className="ml4" />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="graph_area_toggle_open" />
                  )
                }
                {
                  isDisplayGraphDetail && searchResult.summary ? (
                    <>
                      <div className="graph_area">
                        <div className="graph_detail_wrapper">
                          <div className="graph_detail title">
                            <span className="head">
                              {t('search.result.summary.title.time')}
                            </span>
                          </div>
                          <ul className="graph_detail_lists">
                            {
                              [...Array(searchResult.summary.elapsed_time.value_labels.length)].map((_, i) => (
                                searchResult.summary?.elapsed_time.value_labels[i] && (
                                  <li className="graph_detail_list" key={`list_${searchResult.summary.elapsed_time.value_labels[i]}`}>
                                    <div className="name">
                                      {searchResult.summary.elapsed_time.name_labels[i]}
                                    </div>
                                    <div className="bar-graph-wrapper">
                                      <div className="bar-graph">
                                        <div
                                          className="value time_bar"
                                          style={{
                                            width: `${
                                              (searchResult.summary.elapsed_time.value_labels[i]
                                                / maxSummaryElapsedTimeValueLabel) * 100 * 0.96
                                            }%`,
                                          }}
                                        />
                                      </div>
                                      <div className="amount">
                                        {secToHour(searchResult.summary.elapsed_time.value_labels[i] || 0)}
                                        :
                                        {secToTwoDigitsMinute(searchResult.summary.elapsed_time.value_labels[i] || 0)}
                                      </div>
                                    </div>
                                  </li>
                                )
                              ))
                            }
                          </ul>
                        </div>
                        <div className="graph_detail_wrapper">
                          <div className="graph_detail title">
                            <span className="head">
                              {t('search.result.summary.title.biz')}
                            </span>
                          </div>
                          <ul className="graph_detail_lists">
                            {
                              [...Array(searchResult.summary.biz.value_labels.length)].map((_, i) => (
                                searchResult.summary?.biz.value_labels[i] && (
                                  <li className="graph_detail_list" key={`list_${searchResult.summary.biz.value_labels[i]}`}>
                                    <div className="name">
                                      {searchResult.summary.biz.name_labels[i]}
                                    </div>
                                    <div className="bar-graph-wrapper">
                                      <div className="bar-graph">
                                        <div
                                          className="value operation"
                                          style={{
                                            width: `${
                                              (searchResult.summary.biz.value_labels[i]
                                                / maxSummaryBizValueLabel) * 100 * 0.96
                                            }%`,
                                          }}
                                        />
                                      </div>
                                      <div className="amount">
                                        {searchResult.summary.biz.value_labels[i] || 0}
                                        &nbsp;biz
                                      </div>
                                    </div>
                                  </li>
                                )
                              ))
                            }
                          </ul>
                        </div>
                        <div className="graph_detail_wrapper">
                          <div className="graph_detail title">
                            <span className="head">
                              {t('search.result.summary.title.count')}
                            </span>
                          </div>
                          <ul className="graph_detail_lists">
                            {
                              [...Array(searchResult.summary.count.value_labels.length)].map((_, i) => (
                                searchResult.summary?.count.value_labels[i] && (
                                  <li className="graph_detail_list" key={`list_${searchResult.summary.count.value_labels[i]}`}>
                                    <div className="name">
                                      {searchResult.summary.count.name_labels[i]}
                                    </div>
                                    <div className="bar-graph-wrapper">
                                      <div className="bar-graph">
                                        <div
                                          className="value count"
                                          style={{
                                            width: `${
                                              (searchResult.summary.count.value_labels[i]
                                                / maxSummaryCountValueLabel) * 100 * 0.96
                                            }%`,
                                          }}
                                        />
                                      </div>
                                      <div className="amount">
                                        {searchResult.summary.count.value_labels[i] || 0}
                                        &nbsp;
                                        {t('search.result.summary.count.value_labels')}
                                      </div>
                                    </div>
                                  </li>
                                )
                              ))
                            }
                          </ul>
                        </div>
                      </div>
                      <div className="graph_area_button">
                        <button
                          onClick={() => setIsDisplayGraphDetail(!isDisplayGraphDetail)}
                          type="button"
                          className="button"
                        >
                          {t('search.result.summary.graph_area_toggle.close')}
                          <div className="icon">
                            <FontAwesomeIcon icon={['fas', 'angle-up']} className="ml4" />
                          </div>
                        </button>
                      </div>
                    </>
                  ) : (
                    <></>
                  )
                }
              </div>
              <div className="search_result">
                <div className="search_result_header">
                  <div className="search_result_title">
                    {t('search.result.data.title')}
                  </div>
                </div>
                <div className="filter_area">
                  <div className="tag_area">
                    {
                      searchResult.tags.length > 1 && (
                        <div className={`detail ${isDisplayTagButton && !isDisplayTagArea && "thin"}`} ref={tagRef}>
                          {
                            isDisplayTagButton && (
                              <button
                                className={`tag_toggle ${isDisplayTagArea && "close"}`}
                                type="button"
                                onClick={() => setIsDisplayTagArea(!isDisplayTagArea)}
                              >
                                {
                                  isDisplayTagArea ? (
                                    <>
                                      {t('search.result.data.tag.close')}
                                      <FontAwesomeIcon icon={['fas', 'angle-up']} className="ml4" />
                                    </>
                                  ) : (
                                    <>
                                      {t('search.result.data.tag.open')}
                                      <FontAwesomeIcon icon={['fas', 'angle-down']} className="ml4" />
                                    </>
                                  )
                                }
                              </button>
                            )
                          }
                          <div className="tags">
                            {
                              searchResult.tags?.map((result) => (
                                <div
                                  key={result}
                                  className={`tag ${filterList.includes(result) && "active"}`}
                                  onClick={() => buildFilterList(result)}
                                  role="none"
                                >
                                  {result}
                                </div>
                              ))
                            }
                          </div>
                        </div>
                      )
                    }
                  </div>
                  <div className="sort_area">
                    <select
                      className="result_sort"
                      onChange={(e) => sortResultList(e.target.value as SortList)}
                      defaultValue={
                        `${searchValue.sort_key}-${searchValue.sort_type}`
                        || `${searchResult.search_request?.sort_key}-${searchResult.search_request?.sort_type}`
                        || "time"
                      }
                    >
                      <option value="time-desc">{t('search.result.data.sort.desc.time')}</option>
                      <option value="time-asc">{t('search.result.data.sort.asc.time')}</option>
                      <option value="biz-desc">{t('search.result.data.sort.desc.biz')}</option>
                      <option value="biz-asc">{t('search.result.data.sort.asc.biz')}</option>
                      <option value="count-desc">{t('search.result.data.sort.desc.count')}</option>
                      <option value="count-asc">{t('search.result.data.sort.asc.count')}</option>
                      <option value="efficiency-desc">{t('search.result.data.sort.desc.efficiency')}</option>
                      <option value="efficiency-asc">{t('search.result.data.sort.asc.efficiency')}</option>
                      <option value="stress-desc">{t('search.result.data.sort.desc.stress')}</option>
                      <option value="stress-asc">{t('search.result.data.sort.asc.stress')}</option>
                      <option value="focus-desc">{t('search.result.data.sort.desc.focus')}</option>
                      <option value="focus-asc">{t('search.result.data.sort.asc.focus')}</option>
                    </select>
                    <div className="sort_area_icon">
                      <FontAwesomeIcon icon={['fas', 'exchange-alt']} className="ml4" />
                    </div>
                  </div>
                </div>
                <div className="detail_box summary_title">
                  <div className="detail_summary_wrapper">
                    <div className="detail_summary">
                      <div className="detail_header" />
                      <div className="detail_block time">{t('search.result.data.table.time')}</div>
                      <div className="detail_block biz">
                        {t('search.result.data.table.biz')}
                        <span className="detail_summary_table_title">{t('search.result.data.table.total')}</span>
                      </div>
                      <div className="detail_block count">{t('search.result.data.table.count')}</div>
                      <div className="detail_block blank" />
                    </div>
                    {
                      displayGraph && (
                        <div className="detail_summary graph">
                          <div className="detail_block">
                            {t('search.result.data.table.title.business')}
                            <span className="business_efficiency">
                              {t('search.result.data.table.title.efficiency')}
                            </span>
                          </div>
                          <div className="detail_block">
                            {t('search.result.data.table.title.business')}
                            <span className="business_stress">
                              {t('search.result.data.table.title.stress')}
                            </span>
                            <span className="detail_summary_table_title">{t('search.result.data.table.appropriateness')}</span>
                          </div>
                          <div className="detail_block">
                            {t('search.result.data.table.title.business')}
                            <span className="business_focus">
                              {t('search.result.data.table.title.focus')}
                            </span>
                          </div>
                        </div>
                      )
                    }
                  </div>
                </div>
                {
                  isResultLoading ? (
                    <div
                      style={{
                        height: "600px",
                      }}
                    >
                      <Loading />
                    </div>
                  ) : (
                    0 < (displayResultList?.length || 0) && (
                      displayResultList?.map((result, key) => (
                        <AppDetail
                          result={result}
                          request={searchResult.search_request}
                          // eslint-disable-next-line react/no-array-index-key
                          key={`result_${result.service.name}_${result.service.title}_${key}`}
                          displayGraph={displayGraph}
                        />
                      ))
                    )
                  )
                }
              </div>
            </div>
          </section>
        )
      }
      {
        (0 < displayResultListRef.current.length && (displayOffset < resultNum) && !isResultLoading) && (
          <>
            {
              isMoreLoading === true ? (
                <Loading />
              ) : (
                <div className="more_section">
                  <button
                    type="button"
                    onClick={() => clickMoreButton(displayOffset)}
                  >
                    {t('search.result.more_display')}
                  </button>
                </div>
              )
            }
          </>
        )
      }
    </>
  )

  return (
    <>
      {
        isLoading ? (
          <section className="content-header search_result_num center">
            <Loading />
          </section>
        ) : (
          <>
            {
              searchResult.search_result ? (
                renderSearchResult()
              ) : (
                <div>
                  <div className="result_header">
                    <div className="result_title">
                      {t('search.result.error.title')}
                    </div>
                  </div>
                  <div className="result_no_data">
                    {t('search.result.error.description')}
                  </div>
                </div>
              )
            }
          </>
        )
      }
    </>
  )
}

export default SearchResult
